import { useState, useEffect } from "react";
import { LessonTimeLine } from "../../types/newtypes";

export interface UseLessonTimelineProps {
  lessons: LessonTimeLine[];
  completedLessons?: number;
  // eslint-disable-next-line no-unused-vars
  onLessonClick: (event: unknown, lesson: LessonTimeLine) => void;
}

export const useLessonTimeline = ({
  lessons,
  completedLessons = 0,
  onLessonClick,
}: UseLessonTimelineProps) => {
  // Create a new array with lessons + trophy at the end (only if there are lessons)
  const allItems =
    lessons.length > 0
      ? [
        ...lessons,
        {
          id: "trophy",
          title: "Congratulations!",
          status: "locked" as const,
        },
      ]
      : [];

  // Determine if a lesson is available based on status
  const isLessonAvailable = (lessonId: string): boolean => {
    if (lessonId === "trophy") {
      return lessons.length > 0 && completedLessons >= lessons.length;
    }
    const lesson = lessons.find((l) => l.id === lessonId);
    return lesson?.status === "unlocked" || lesson?.status === "completed";
  };

  // Find the first lesson that is available but not completed to set as active
  const findActiveLesson = () => {
    const nextAvailableLesson = lessons.find(
      (lesson) => lesson.status === "unlocked"
    );

    return nextAvailableLesson?.id || lessons[0]?.id || "";
  };

  const [activeLessonId, setActiveLessonId] =
    useState<string>(findActiveLesson());
  const [clickedLesson, setClickedLesson] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState<string | null>(null);
  const [showUnlockIcon, setShowUnlockIcon] = useState<string | null>(null);
  const [activeBubbleId, setActiveBubbleId] = useState<string | null>(null);
  const [celebratedLessons, setCelebratedLessons] = useState<Set<string>>(
    new Set()
  );

  // Calculate progress percentage
  const progressPercentage = lessons.length > 0 ? Math.min(100, (completedLessons / lessons.length) * 100) : 0;

  // Simple function to check if a lesson is completed based on status
  const isLessonComplete = (lessonId: string): boolean => {
    if (lessonId === "trophy") {
      return lessons.length > 0 && completedLessons >= lessons.length;
    }
    const lesson = lessons.find((l) => l.id === lessonId);
    return lesson?.status === "completed";
  };

  // Check if all lessons are completed
  const allLessonsCompleted = lessons.length > 0 && completedLessons >= lessons.length;

  // Update active lesson when completedLessons changes
  useEffect(() => {
    const nextAvailableLesson = lessons.find(
      (lesson) => lesson.status === "unlocked"
    );
    setActiveLessonId(nextAvailableLesson?.id || lessons[0]?.id || "");
  }, [completedLessons, lessons]);

  // Effect to show celebration when completedLessons changes
  useEffect(() => {
    if (completedLessons > 0) {
      const lastCompletedLesson = lessons[completedLessons - 1];

      if (
        lastCompletedLesson &&
        !celebratedLessons.has(lastCompletedLesson.id)
      ) {
        // Show celebration for newly completed lesson
        setShowCelebration(lastCompletedLesson.id);

        // Add to celebrated lessons set
        setCelebratedLessons((prev) =>
          new Set(prev).add(lastCompletedLesson.id)
        );

        // Hide celebration after 2 seconds
        setTimeout(() => {
          setShowCelebration(null);
        }, 2000);
      }
    }
  }, [completedLessons, lessons, celebratedLessons]);

  // Effect to show celebration for trophy when all lessons are completed
  useEffect(() => {
    if (allLessonsCompleted && !celebratedLessons.has("trophy")) {
      // Show trophy celebration
      setShowCelebration("trophy");

      // Add to celebrated lessons set
      setCelebratedLessons((prev) => new Set(prev).add("trophy"));

      // Hide celebration after 2 seconds
      setTimeout(() => {
        setShowCelebration(null);
      }, 2000);
    }
  }, [allLessonsCompleted, celebratedLessons]);

  // Show bubble for active lesson and hide after 4 seconds
  useEffect(() => {
    setActiveBubbleId(activeLessonId);
    const timer = setTimeout(() => {
      setActiveBubbleId(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [activeLessonId]);

  const handleLessonClick = (event: unknown, lesson: LessonTimeLine) => {
    if (isLessonAvailable(lesson.id)) {
      // First check if this is a newly available lesson
      // that wasn't previously selected
      const isNewlyAvailable =
        isLessonAvailable(lesson.id) &&
        !isLessonComplete(lesson.id) &&
        lesson.id !== activeLessonId;

      // Show unlock icon for newly available lessons
      if (isNewlyAvailable) {
        setShowUnlockIcon(lesson.id);
        // Hide unlock icon after 2 seconds
        setTimeout(() => {
          setShowUnlockIcon(null);
        }, 2000);
      }

      setActiveLessonId(lesson.id);
      setActiveBubbleId(lesson.id);

      // Reset bubble timer when clicking a new lesson
      setTimeout(() => {
        if (activeBubbleId === lesson.id) {
          setActiveBubbleId(null);
        }
      }, 4000);

      // Set clicked state for animation
      setClickedLesson(lesson.id);
      setTimeout(() => {
        setClickedLesson(null);
      }, 300);

      onLessonClick(event, lesson);
    }
  };

  // Function to get bubble position based on lesson
  const getBubblePosition = (lessonId: string) => {
    const lessonIndex = lessons.findIndex((lesson) => lesson.id === lessonId);

    if (lessonIndex === -1) {
      return "left-[50%] top-[-30px] transform -translate-x-1/2"; // Default position
    }

    // Calculate the row and position (left or right) based on index
    const isLeft = lessonIndex % 2 === 0;

    // Adjust left position based on whether it's on the left or right
    const leftPosition = isLeft ? "50%" : "50%";
    const transform = "transform -translate-x-1/2";

    return `left-[${leftPosition}] bottom-[30px] ${transform}`;
  };

  // Function to get bubble content based on lesson
  const getBubbleContent = (lessonId: string) => {
    const lessonIndex = lessons.findIndex((lesson) => lesson.id === lessonId);

    if (lessonIndex === -1 || lessonIndex === 0) {
      // First lesson - default content
      return {
        title: "Welcome on board!",
        subtitle: "Let's get started and have fun along the way!",
      };
    } else if (lessonIndex === lessons.length - 1) {
      // Last lesson (not trophy)
      return {
        title: "You are doing great!",
        subtitle: "Finish your remaining units to complete this module.",
      };
    } else {
      // Middle lessons
      return {
        title: "You are doing great!",
        subtitle: `Continue your remaining units in this module.`,
      };
    }
  };

  const initialConnectorActive = lessons[0]?.id;

  return {
    allItems,
    isLessonAvailable,
    isLessonComplete,
    allLessonsCompleted,
    activeLessonId,
    clickedLesson,
    showCelebration,
    showUnlockIcon,
    activeBubbleId,
    progressPercentage,
    handleLessonClick,
    getBubblePosition,
    getBubbleContent,
    initialConnectorActive,
  };
};
